import { ref, watch, type Ref } from 'vue';
import { useLocalStorage } from './useLocalStorage';
import type { Column } from '@apparatix/types';

type Options = {
  visibleItemsKey: string;
  columnOrderKey: string;
  ref: Ref<any>;
  defaultColumns: Column[];
}

export const useColumnConfig = (options: Options) => {
  const { getItem } = useLocalStorage();
  const columnOrder = ref<Column[]>([]);
  const visibleColumns = ref<string[]>([]);

  const savedVisible = getItem<string[]>(options.visibleItemsKey);
  const savedOrder = getItem<Column[]>(options.columnOrderKey);

  if (savedVisible) {
    visibleColumns.value = savedVisible;
  } else {
    visibleColumns.value = options.defaultColumns.filter(col => col.visible).map(col => col.field);
  }

  // Check if new columns have been added since the user saved their column order. If we have added new columns add them
  // to the list of columns and set it visible or invisible based on our recommendations for that column.
  if (savedOrder) {
    const missingColumns = options.defaultColumns.filter(col =>
      !savedOrder.some(saved => saved.field === col.field),
    );

    if (missingColumns.length > 0) {
      const visibleNewFields = missingColumns
        .filter(col => col.visible)
        .map(col => col.field);

      visibleColumns.value = [...visibleColumns.value, ...visibleNewFields];
    }

    columnOrder.value = [...savedOrder, ...missingColumns];
  } else {
    columnOrder.value = options.defaultColumns;
  }

  watch(() => options.ref.value?.d_columnOrder, () => {
    if (options.ref.value && !options.ref.value.d_columnOrder) {
      // eslint-disable-next-line camelcase
      options.ref.value.d_columnOrder = columnOrder.value.map(col => col.field);
    }
  });

  const handleVisibleColumnChange = (updatedColumns: string[]) => visibleColumns.value = updatedColumns;

  const handleColumnOrderChange = (updatedColumns: Column[]) => {
    columnOrder.value = updatedColumns;

    if (!options.ref.value?.d_columnOrder) return;
    // eslint-disable-next-line camelcase
    options.ref.value.d_columnOrder = updatedColumns.map(col => col.field);
  };

  return {
    columnOrder,
    handleColumnOrderChange,
    handleVisibleColumnChange,
    visibleColumns,
  };
};

export const useBuildDefaultColumns = (columns: Array<[string, string, boolean?]>): Column[] =>
  columns.map(column => ({
    field: column[0],
    header: column[1],
    visible: typeof column[2] === 'boolean' ? column[2] : true,
  }));
